// src/pages/CreateAssessment.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { assessmentsApi, clientsApi, assessmentTypesApi } from '../services/api';

const CreateAssessment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('client_id');

  const [clients, setClients] = useState([]);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    client_id: preselectedClientId || '',
    assessment_type_id: '',
    name: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load clients for dropdown
      const clientsResponse = await clientsApi.getAll({ active: true });
      setClients(clientsResponse.data.clients || []);

      // Load assessment types
      const typesResponse = await assessmentTypesApi.getAll();
      setAssessmentTypes(typesResponse.data.assessment_types || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load required data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    console.log('Creating assessment with data:', formData);

    try {
      const response = await assessmentsApi.create(formData);
      console.log('Assessment created successfully:', response.data);
      // Redirect to the new assessment details page
      // Note: backend returns the assessment directly, not wrapped in { assessment: {...} }
      navigate(`/assessments/${response.data.id}`);
    } catch (error) {
      console.error('Full error creating assessment:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create assessment';
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (preselectedClientId) {
      // Go back to the client details page
      navigate(`/clients/${preselectedClientId}`);
    } else {
      // Go to assessments list
      navigate('/assessments');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '30px' }}>Create New Assessment</h1>

      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {error && (
          <div style={{
            padding: '10px',
            marginBottom: '20px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c00'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Client *</label>
            <select
              name="client_id"
              value={formData.client_id}
              onChange={handleInputChange}
              required
              disabled={!!preselectedClientId}
              style={{
                ...inputStyle,
                backgroundColor: preselectedClientId ? '#f5f5f5' : 'white',
                cursor: preselectedClientId ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="">Select a client...</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
            {preselectedClientId && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px', fontStyle: 'italic' }}>
                Client pre-selected from previous page
              </div>
            )}
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Assessment Type *</label>
            <select
              name="assessment_type_id"
              value={formData.assessment_type_id}
              onChange={handleInputChange}
              required
              style={inputStyle}
            >
              <option value="">Select an assessment type...</option>
              {assessmentTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Assessment Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="e.g., Q1 2026 NIST Assessment"
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              placeholder="Optional description or notes..."
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '30px' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                opacity: submitting ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '10px 20px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                opacity: submitting ? 0.5 : 1
              }}
            >
              {submitting ? 'Creating...' : 'Create Assessment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const formGroupStyle = {
  marginBottom: '20px'
};

const labelStyle = {
  display: 'block',
  marginBottom: '5px',
  fontWeight: 'bold',
  fontSize: '14px'
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

export default CreateAssessment;
